import { ReactElement, useContext, useState } from "react";
import { ICatalogCategory, ICategoryField, IFamilyBase } from "../../types";
import TableContainer from "../../../../components/tableContainer";
import {
  FormControl,
  IconButton, InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow, TextField,
  Tooltip
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../../configuration/configuration";
import StyledTableRow from "../../../../components/styledTableRow";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IContentAreaProps } from "../../../../types";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CustomConfigDialog from "./customConfigDialog";
import CommentIcon from "@mui/icons-material/Comment";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CatalogCategoryDialog from "./catergoryDialog";
import { SystemContext } from "../../../../context/systemContext";


interface IProps extends IContentAreaProps {
  category: ICatalogCategory;
  updateCategory: (category: ICatalogCategory) => void;
  wasEdited: boolean;
  setWasEdited: (edited: boolean) => void;
  family: IFamilyBase;
  allowEdit: boolean;
}

interface IVariableRowProps {
  index: number;
  data: ICategoryField;
  setData: (data: ICategoryField) => void;
  onRemoveField: (index: number, fieldName: string) => void;
  family: IFamilyBase;
  allowEdit: boolean;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
}

const FieldRow = ({ index, data, setData, onRemoveField, family, allowEdit, selectedIndex, setSelectedIndex }: IVariableRowProps): ReactElement => {
  const [force, setForce] = useState(0);
  const [editCustom, setEditCustom] = useState(false);

  const updateRow = (field: keyof ICategoryField, value: any) => {
    if (field === "name") {
      data[field] = value;
    } else if (field === "dataType") {
      data[field] = value;
      if(value === "TEXT" || value === "BOOL") {
        delete data.minimum;
        delete data.maximum;
      } else if (value === "NUMBER") {
        data.value = 0;
        delete data.minimum;
        delete data.maximum;
      }
    } else if (field === "value") {
      data[field] = value;
    } else if(field === "minimum") {
      data[field] = value;
    } else if(field === "maximum") {
      data[field] = value;
    } else if(field === "unit") {
      data[field] = value;
    }

    setData(data);
    setForce((force + 1) % 10 );
  };

  const onAccept = (editedData: ICategoryField) => {
    data.description = editedData.description;
    data.custom = editedData.custom;
    data.customOptions = editedData.customOptions;
    setData(data);
    setEditCustom(false);
    setSelectedIndex(-1);
  };

  const onCancel = () => {
    setEditCustom(false);
    setSelectedIndex(-1);
  };

  const onDisableMin = () => {
    data.minimum = data.minimum !== undefined ? undefined : 0;
    setData(data);
    setForce((force + 1) % 10 );
  };

  const onDisableMax = () => {
    data.maximum = data.maximum !== undefined ? undefined : 100;
    setData(data);
    setForce((force + 1) % 10 );
  };

  const onDisableValue = () => {
    data.value = data.value !== null ? null : 0;
    setData(data);
    setForce((force + 1) % 10 );
  };

  const onCustomEdit = () => {
    setEditCustom(true);
    setSelectedIndex(index);
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} selected={index === selectedIndex}>
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        <div className="display-flex-row align-items-center">
          {index + 1}
        </div>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <TextField
            variant="standard"
            size="small"
            value={data.name}
            type="text"
            slotProps={{ input: {
              readOnly:  !allowEdit,
              endAdornment: data.description && data.description !== ""
                ? <InputAdornment position="end">
                  <Tooltip title={data.description}>
                    <CommentIcon />
                  </Tooltip>
                </InputAdornment>
                : undefined }}}
            onChange={(event) => updateRow("name",  event.target.value as string)}
          />
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.dataType}
            displayEmpty={true}
            disabled={!allowEdit}
            onChange={(event: SelectChangeEvent) => updateRow("dataType", event.target.value as string)}>
            <MenuItem value="TEXT">TEXT</MenuItem>
            <MenuItem value="NUMBER">NUMBER</MenuItem>
            {/*<MenuItem value="BOOL">BOOL</MenuItem>*/}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell colSpan={data.dataType === "NUMBER" ? 1 :4}>
        <FormControl className="w-100">
          {data.dataType === "NUMBER"
            ? <TextField
              variant="standard"
              size="small"
              value={data.value}
              type="number"
              disabled={data.value === null}
              slotProps={{
                input: {
                  readOnly: !allowEdit,
                  inputProps: { min: data.minimum, max: data.maximum },
                  startAdornment:
                    <InputAdornment position="end">
                      <IconButton
                        disabled={!allowEdit}
                        onClick={onDisableValue} >
                        {data.value === null ? <CheckBoxOutlineBlankIcon /> : <CheckBoxIcon />}
                      </IconButton>
                    </InputAdornment>
                }
              }}
              onChange={(event) => updateRow("value", parseFloat(event.target.value as string))}/>
            : <TextField
              variant="standard"
              size="small"
              value={data.value}
              type="text"
              slotProps={{ input: { readOnly: !allowEdit }}}
              onChange={(event) => updateRow("value", event.target.value as string)}/>
          }
        </FormControl>
      </TableCell>

      {data.dataType === "NUMBER" &&
        <TableCell>
          <FormControl className="w-100">
            <TextField
              variant="standard"
              size="small"
              value={data.minimum ?? null}
              type="number"
              disabled={data.minimum === undefined}
              slotProps={{
                input: {
                  readOnly: !allowEdit,
                  inputProps: { max: data.maximum },
                  startAdornment:
                  <InputAdornment position="end">
                    <IconButton
                      disabled={!allowEdit}
                      onClick={onDisableMin} >
                      {data.minimum === undefined ? <CheckBoxOutlineBlankIcon /> : <CheckBoxIcon />}
                    </IconButton>
                  </InputAdornment>
              }}}
              onChange={(event) => updateRow("minimum", parseFloat(event.target.value as string))}/>
          </FormControl>
        </TableCell>
      }

      {data.dataType === "NUMBER" &&
        <TableCell>
          <FormControl className="w-100">
            <TextField
              variant="standard"
              size="small"
              value={data.maximum ?? null}
              type="number"
              disabled={data.maximum === undefined}
              slotProps={{
                input: {
                readOnly: !allowEdit,
                inputProps: { min: data.minimum },
                startAdornment:
                <InputAdornment position="end">
                  <IconButton
                    disabled={!allowEdit}
                    onClick={onDisableMax} >
                    {data.maximum === undefined  ? <CheckBoxOutlineBlankIcon /> : <CheckBoxIcon />}
                  </IconButton>
                </InputAdornment>
              }}}
              onChange={(event) => updateRow("maximum", parseFloat(event.target.value as string))}/>
          </FormControl>
        </TableCell>
      }

      {data.dataType === "NUMBER" &&
        <TableCell>
          <FormControl className="w-100">
            <TextField
              variant="standard"
              size="small"
              value={data.unit ?? ""}
              type="text"
              slotProps={{ input: {readOnly: !allowEdit }}}
              onChange={(event) => updateRow("unit", event.target.value as string)}
            />
          </FormControl>
        </TableCell>
      }
      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }} onClick={onCustomEdit}>
          <Tooltip title={"Custom Properties"}>
            <MoreVertIcon style={{ color: getPalette().statusColor.info.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

      <TableCell>
        {allowEdit &&
          <IconButton style={{ padding: 0, margin: 0 }} onClick={() => onRemoveField(index, data.name)}>
            <Tooltip title={"Remove variable"}>
              <Delete style={{ color: getPalette().statusColor.error.background }}/>
            </Tooltip>
          </IconButton>
        }
      </TableCell>

      {editCustom && <CustomConfigDialog data={data} family={family} onAccept={onAccept} onCancel={onCancel} allowEdit={allowEdit}/>}
    </StyledTableRow>
  );
};

const CategoryContent = ({ category, updateCategory, family, allowEdit, parentSize }: IProps): ReactElement => {
  const systemContext = useContext(SystemContext);
  const [ selectedIndex, setSelectedIndex ] = useState(-1);
  const [ editProduct, setEditProduct ] = useState(false);

  const addField = () => {
    if (category && category.categoryID){
      category.content.fields.push({
        name: "field",
        dataType: "NUMBER",
        value: 0
      });

      updateCategory(category);
    }
  };

  const onRemoveField = (index: number, fieldName: string) => {
    systemContext.showMessage({
      title: "Delete",
      color: "danger",
      message: `Do you want to delete the Field ${fieldName}?`,
      callback: () => {
        category.content.fields.splice(index, 1);
        updateCategory(category);
      }
    });
  };

  const onUpdateData = () => {
    updateCategory(category);
  };

  const onChange = (value: string) => {
    category.name = value;
    updateCategory(category);
  };


  const onEditProducts = () => {
    setEditProduct(true);
  };

  const onCancelEdit = () => {
    setEditProduct(false);
  };

  const onAcceptEdit = (editedCategory: ICatalogCategory) => {
    setEditProduct(false);

    // updateCategory(editedCategory);
  };

  return (
    <div key={`category-${category.categoryID}`} className="display-grid" style={{ gridTemplateRows: "auto 1fr", height: parentSize.height - 222 }}>
      <div className="mb-1 display-flex-row gap-1 align-items-center">
        <TextField
          className="m-1"
          label="Name"
          style={{ width: 500 }}
          variant="standard"
          size="small"
          value={category.name}
          type="text"
          slotProps={{ input: {readOnly: !allowEdit }}}
          onChange={(event) => onChange(event.target.value as string)}
        />

        <Tooltip title="Products">
          <IconButton aria-label="remove" onClick={onEditProducts} color="info" >
            <ChecklistIcon />
          </IconButton>
        </Tooltip>
      </div>
      <TableContainer>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
                {allowEdit &&
                  <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }} onClick={addField}>
                    <Tooltip title={"Add Field"}>
                      <Add style={{ color: getPalette().statusColor.info.background }}/>
                    </Tooltip>
                  </IconButton>
                }
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 300, maxWidth: 300 }}>
                Name
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 110, maxWidth: 110 }}>
                Type
              </TableCell>
              <TableCell className="table-header">Value / Aim / SP</TableCell>
              <TableCell className="table-header">Minimum</TableCell>
              <TableCell className="table-header">Maximum</TableCell>
              <TableCell className="table-header" style={{ minWidth: 80, maxWidth: 80 }}>Unit</TableCell>
              <TableCell className="table-header" style={{ minWidth: 80, maxWidth: 80 }}>Custom</TableCell>
              <TableCell className="table-header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {category.content.fields.map((row, index) => {
              return (
                <FieldRow key={`field-row-${index}`}
                  onRemoveField={onRemoveField}
                  index={index}
                  data={row} setData={onUpdateData}
                  family={family}
                  allowEdit={allowEdit}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}/>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      { editProduct && <CatalogCategoryDialog onCancel={onCancelEdit} onAccept={onAcceptEdit} category={category} /> }
    </div>
  );
};

export default CategoryContent;